// clinic-time.js — CLIENT twin of server/opd/clinic-time.mjs.
//
// The online-primary app shapes Appointment/Encounter times in the BROWSER now
// (lib/shape/*), so the same clinic-timezone discipline the server enforces must
// live here too. Appointment.start/end are stored as REAL UTC instants (FHIR
// `instant`); to render them at the clinic's wall-clock — regardless of the
// viewer's device timezone — we must format through the clinic IANA zone, never
// string-slice the ISO ("…T04:00Z".slice(11,16) prints 04:00, not 09:30 IST).
//
// ONE config point: the clinic timezone is set once from the loaded hospital
// settings (setClinicTimezone), defaulting to Asia/Kolkata. India has no DST, so
// a fixed +05:30 would do — but we derive the offset from the IANA zone via Intl
// so a hospital that runs in another timezone stays correct with no code change.

const DEFAULT_TZ = 'Asia/Kolkata';
let clinicTz = DEFAULT_TZ;

/** Configure the clinic timezone once, from hospital settings (App bootstrap). */
export function setClinicTimezone(tz) {
  const v = tz && String(tz).trim();
  if (v) clinicTz = v;
  return clinicTz;
}
export function getClinicTimezone() { return clinicTz; }

// Minutes that `tz` is AHEAD of UTC at the given instant (IST → +330).
function tzOffsetMinutes(tz, atDate) {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const p = dtf.formatToParts(atDate).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
    let hh = +p.hour;
    if (hh === 24) hh = 0; // some engines emit 24 for midnight
    const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, hh, +p.minute, +p.second);
    return Math.round((asUTC - atDate.getTime()) / 60000);
  } catch {
    return 330; // IST fallback
  }
}

/**
 * Clinic wall-clock (date "YYYY-MM-DD" + "HH:mm") → real UTC ISO instant.
 * e.g. ("2026-06-14","09:00","Asia/Kolkata") → "2026-06-14T03:30:00.000Z".
 * Use this to COMPOSE a stored Appointment.start from a picked date + time.
 */
export function wallClockToInstant(dateISO, hhmm, tz = clinicTz) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || '').trim());
  const [y, mo, d] = String(dateISO).split('-').map(Number);
  if (!m || !y || !mo || !d) return null;
  const hh = +m[1]; const mm = +m[2];
  const approx = new Date(Date.UTC(y, mo - 1, d, hh, mm, 0, 0));
  const off = tzOffsetMinutes(tz, approx);
  return new Date(approx.getTime() - off * 60000).toISOString();
}

// "HH:mm" (24h) in the clinic tz for a real instant. Empty string for bad input.
export function formatClinicTime(iso, tz = clinicTz) {
  if (!iso) return '';
  const dt = new Date(iso);
  if (isNaN(dt)) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(dt);
  } catch {
    return '';
  }
}

/**
 * Any Intl date/time format, PINNED to the clinic timezone (2026-08-05 W2b).
 *
 * The screens each grew their own `new Date(iso).toLocaleDateString('en-IN', …)`
 * helper, which renders in the DEVICE timezone — a tablet or phone with a
 * mis-set clock then files a visit under the wrong calendar date. Pass the SAME
 * Intl options here and the ONLY thing that changes is the zone.
 *
 * `value` may be an ISO string, epoch ms, or a Date. `locale` takes [] when the
 * caller deliberately renders in the viewer's own locale (ward/handover screens).
 * Returns '' for empty/unparseable input so callers keep their own fallback.
 */
export function formatInClinicTz(value, options = {}, locale = 'en-IN', tz = clinicTz) {
  if (value == null || value === '') return '';
  const dt = value instanceof Date ? value : new Date(value);
  if (isNaN(dt.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: tz }).format(dt);
  } catch {
    return '';
  }
}

// "YYYY-MM-DD" in the clinic tz for a real instant (for day-bucketing).
export function clinicDateKey(iso, tz = clinicTz) {
  if (!iso) return '';
  const dt = new Date(iso);
  if (isNaN(dt)) return '';
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(dt);
  } catch {
    return String(iso).slice(0, 10);
  }
}

// "Today" as a clinic-local calendar date. `now` may be epoch ms, Date, or ISO.
export function clinicTodayKey(now = Date.now(), tz = clinicTz) {
  const dt = now instanceof Date ? now : new Date(now);
  if (isNaN(dt)) return clinicDateKey(new Date().toISOString(), tz);
  return clinicDateKey(dt.toISOString(), tz);
}

// Add whole calendar days to a YYYY-MM-DD clinic date (UTC arithmetic on the
// calendar label is host-timezone-safe).
export function clinicDatePlusDays(dateISO, days = 0) {
  const [y, mo, d] = String(dateISO || '').slice(0, 10).split('-').map(Number);
  if (!y || !mo || !d) return '';
  const delta = Number(days) || 0;
  return new Date(Date.UTC(y, mo - 1, d + delta)).toISOString().slice(0, 10);
}

// UTC instant bounds for one clinic-local day: [start, end). Use for a search
// window that must cover exactly the clinic's calendar day.
export function clinicDayBounds(dateISO, tz = clinicTz) {
  const day = String(dateISO || clinicTodayKey(Date.now(), tz)).slice(0, 10);
  const next = clinicDatePlusDays(day, 1);
  return {
    start: wallClockToInstant(day, '00:00', tz),
    end: wallClockToInstant(next, '00:00', tz),
  };
}

/**
 * Day of an in-patient stay on the CLINIC's calendar — admit day = day 1
 * (2026-08-05 W2.1). `null` for an absent/unparseable admit instant; never
 * below 1 (a future admit still reads "Day 1", matching the four ward copies
 * this replaced).
 *
 * Why it moved here: the ward screens each carried a byte-identical private
 * `dayOfStay` that built its day boundary from the DEVICE calendar
 * (`new Date(start).getFullYear()/getMonth()/getDate()` vs the same on `now`).
 * `Encounter.period.start` is a real UTC instant, so a patient admitted at
 * 01:00 IST is 19:30 the PREVIOUS day in UTC — a tablet on the wrong timezone
 * therefore showed the ward a different day number for the same admission, and
 * the number rolled over at the device's midnight rather than the clinic's.
 * Both ends are now bucketed through `clinicDateKey`/`clinicTodayKey`, so the
 * count is the clinic's own calendar regardless of the device.
 *
 * Callers: screens/ward/WardPatients.jsx, WardBoard.jsx, BedWorkspace.jsx and
 * bedside/bedside-common.jsx (which re-exports it as `dayOfStay` for the
 * bedside shell/glance screens).
 */
export function clinicDayOfStay(startISO, now = Date.now(), tz = clinicTz) {
  const startKey = clinicDateKey(startISO, tz);
  if (!startKey) return null;
  const todayKey = clinicTodayKey(now, tz);
  const startMs = Date.parse(startKey + 'T00:00:00Z');
  const todayMs = Date.parse(todayKey + 'T00:00:00Z');
  if (Number.isNaN(startMs) || Number.isNaN(todayMs)) return null;
  const days = Math.round((todayMs - startMs) / 86400000) + 1;
  return days >= 1 ? days : 1;
}

// Minutes-of-day (0..1439) in the clinic tz — for calendar-grid positioning.
export function clinicMinutesOfDay(iso, tz = clinicTz) {
  const hhmm = formatClinicTime(iso, tz);
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  return (+m[1]) * 60 + (+m[2]);
}
